/**
 * Phase B.3 — accumulated contact portrait.
 *
 * Collapses every `ContactSignal` we've ever recorded for a contact
 * into a single scannable dossier: current job / city / school (latest
 * wins), accumulated interests / skills / beliefs / preferences /
 * relationships (deduped, mention-count sorted), and a life-events
 * timeline.
 *
 * Mental model: a CIA case file, not a profile form. The user never
 * types any of this — it's extracted from natural conversation by the
 * session-level fact extractor and accumulates silently. Every field
 * shown is grounded in a real quote from a real transcript.
 *
 * Renders nothing when the portrait is empty so the parent doesn't
 * have to gate it.
 */
import { useEffect, useState } from "react";
import {
  Briefcase,
  GraduationCap,
  Heart,
  MapPin,
  Quote,
  Sparkles,
  Star,
  Users,
  Wrench,
  Zap,
} from "lucide-react";

import type { ContactSignal } from "@/types/voice";
import { listSignalsForContact } from "@/hooks/useSessionProcessor";
import {
  buildContactPortrait,
  type AccumulatedSignal,
  type ContactPortrait as Portrait,
  type LatestSignal,
  type TimelineSignal,
} from "@/utils/contactPortrait/contactPortrait";

interface ContactPortraitProps {
  contactId: string | null | undefined;
  firstName: string;
}

export function ContactPortrait({ contactId, firstName }: ContactPortraitProps) {
  const [signals, setSignals] = useState<ContactSignal[] | null>(null);

  useEffect(() => {
    if (!contactId) {
      setSignals([]);
      return;
    }
    let cancelled = false;
    listSignalsForContact(contactId)
      .then((rows) => {
        if (!cancelled) setSignals(rows);
      })
      .catch(() => {
        if (!cancelled) setSignals([]);
      });
    return () => {
      cancelled = true;
    };
  }, [contactId]);

  if (signals === null) return null;
  const portrait = buildContactPortrait(signals);
  if (portrait.isEmpty) return null;

  return (
    <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-[rgba(120,180,255,0.18)] bg-[rgba(120,180,255,0.04)] p-4">
      <PortraitHeader portrait={portrait} firstName={firstName} />

      <StatefulRow
        icon={<Briefcase className="h-4 w-4" />}
        label="Works at"
        signal={portrait.currentJob}
      />
      <StatefulRow
        icon={<GraduationCap className="h-4 w-4" />}
        label="Studies at"
        signal={portrait.currentSchool}
      />
      <StatefulRow
        icon={<MapPin className="h-4 w-4" />}
        label="Lives in"
        signal={portrait.currentCity}
      />

      <AccumulatedBlock
        icon={<Sparkles className="h-4 w-4" />}
        label="Interests"
        items={portrait.interests}
      />
      <AccumulatedBlock
        icon={<Users className="h-4 w-4" />}
        label="Relationships"
        items={portrait.relationships}
      />
      <AccumulatedBlock
        icon={<Wrench className="h-4 w-4" />}
        label="Skills"
        items={portrait.skills}
      />
      <AccumulatedBlock
        icon={<Star className="h-4 w-4" />}
        label="Preferences"
        items={portrait.preferences}
      />
      <AccumulatedBlock
        icon={<Heart className="h-4 w-4" />}
        label="Beliefs"
        items={portrait.beliefs}
      />

      <LifeEventsBlock events={portrait.lifeEvents} />
    </div>
  );
}

function PortraitHeader({
  portrait,
  firstName,
}: {
  portrait: Portrait;
  firstName: string;
}) {
  return (
    <div className="flex items-center justify-between">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[rgba(120,180,255,0.8)]">
        What we know about {firstName}
      </p>
      <span className="text-[10px] text-[rgba(255,255,255,0.4)]">
        {portrait.sessionCount} session
        {portrait.sessionCount === 1 ? "" : "s"}
      </span>
    </div>
  );
}

function StatefulRow({
  icon,
  label,
  signal,
}: {
  icon: React.ReactNode;
  label: string;
  signal: LatestSignal | null;
}) {
  if (!signal) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2 text-[14px] text-[rgba(255,255,255,0.9)]">
        <span className="text-[rgba(120,180,255,0.75)]">{icon}</span>
        <span className="text-[rgba(255,255,255,0.55)]">{label}</span>
        <span className="font-medium">{signal.value}</span>
      </div>
      {signal.sourceQuote && <SourceQuote text={signal.sourceQuote} />}
    </div>
  );
}

function AccumulatedBlock({
  icon,
  label,
  items,
}: {
  icon: React.ReactNode;
  label: string;
  items: AccumulatedSignal[];
}) {
  if (items.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[rgba(120,180,255,0.75)]">{icon}</span>
        <p className="text-[11px] uppercase tracking-[0.15em] text-[rgba(255,255,255,0.5)]">
          {label}
        </p>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {items.map((item) => (
          <SignalChip key={item.key} item={item} />
        ))}
      </div>
    </div>
  );
}

function SignalChip({ item }: { item: AccumulatedSignal }) {
  const [expanded, setExpanded] = useState(false);
  const hasQuote = item.quotes.length > 0;
  return (
    <button
      type="button"
      onClick={hasQuote ? () => setExpanded((p) => !p) : undefined}
      className="group flex items-center gap-1.5 rounded-full border border-[rgba(120,180,255,0.18)] bg-[rgba(120,180,255,0.06)] px-3 py-1 text-[12px] text-[rgba(255,255,255,0.85)] active:opacity-70"
    >
      <span>{item.value}</span>
      {item.mentionCount > 1 && (
        <span className="text-[10px] text-[rgba(255,255,255,0.45)]">
          ×{item.mentionCount}
        </span>
      )}
      {expanded && hasQuote && (
        <span className="max-w-[200px] truncate text-[10px] italic text-[rgba(255,255,255,0.55)]">
          “{item.quotes[0]}”
        </span>
      )}
    </button>
  );
}

function LifeEventsBlock({ events }: { events: TimelineSignal[] }) {
  if (events.length === 0) return null;
  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-2">
        <span className="text-[rgba(120,180,255,0.75)]">
          <Zap className="h-4 w-4" />
        </span>
        <p className="text-[11px] uppercase tracking-[0.15em] text-[rgba(255,255,255,0.5)]">
          Life events
        </p>
      </div>
      <ul className="flex flex-col gap-1.5">
        {events.map((e) => (
          <li key={`${e.sessionId}-${e.createdAt}`} className="flex flex-col gap-0.5">
            <span className="text-[13px] text-[rgba(255,255,255,0.9)]">
              {e.value}
            </span>
            {e.sourceQuote && <SourceQuote text={e.sourceQuote} />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function SourceQuote({ text }: { text: string }) {
  return (
    <div className="flex items-start gap-1.5 pl-6 text-[11px] italic text-[rgba(255,255,255,0.45)]">
      <Quote className="mt-0.5 h-2.5 w-2.5 flex-shrink-0" />
      <span>{text}</span>
    </div>
  );
}
